import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import {
  Page,
  Card,
  Banner,
  Button as PolarisButton,
  Text,
  Badge as PolarisBadge,
  Spinner,
} from "@shopify/polaris";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxOptions,
  setModalxStyle,
} from "components/modalx/modalx.slice";
import { API_URL } from "../../constants";
import {
  completeTrainingAssignment,
  fetchTrainingAssignmentDetail,
  TrainingAssignment,
} from "services/work-security-training.service";

type WorkerStep = "training" | "test";

function statusLabel(t: any, status: string) {
  switch (status) {
    case "passed":
      return t("test_passed", { defaultValue: "Passed" });
    case "failed":
      return t("test_failed", { defaultValue: "Failed" });
    case "in_progress":
      return t("training_status_in_progress", {
        defaultValue: "In progress",
      });
    case "pending":
      return t("training_status_pending", { defaultValue: "Pending" });
    default:
      return status;
  }
}

export default function TrainingAssignmentContainer() {
  const { companyId, assignmentId } = useParams<{
    companyId: string;
    assignmentId: string;
  }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();

  const stepParam = searchParams.get("step");
  const currentStep: WorkerStep =
    stepParam === "test" ? "test" : "training";

  const [assignment, setAssignment] = useState<TrainingAssignment | null>(
    null
  );
  const [materials, setMaterials] = useState<
    Array<{ id: string; fileId: string; fileName: string; createdAt?: number }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!companyId || !assignmentId) return;
    setLoading(true);
    setError("");
    try {
      const data = await fetchTrainingAssignmentDetail(
        companyId,
        assignmentId
      );
      setAssignment(data.assignment);
      setMaterials(data.materials || []);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          t("load_failed", { defaultValue: "Failed to load" })
      );
    } finally {
      setLoading(false);
    }
  }, [companyId, assignmentId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const goToStep = (step: WorkerStep) => {
    setSearchParams({ step }, { replace: true });
  };

  const trainingCompleted = !!assignment?.trainingCompleted;
  const canAccessTest = trainingCompleted || !!assignment?.openAttempt;
  const materialsCount =
    assignment?.materialsCount ?? materials.length;

  const handleCompleteTraining = async () => {
    if (!companyId || !assignmentId) return;
    setBusy(true);
    setError("");
    try {
      const data = await completeTrainingAssignment(companyId, assignmentId);
      setAssignment(data.assignment);
      setMaterials(data.materials || []);
      goToStep("test");
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          t("complete_training_failed", {
            defaultValue: "Could not mark training as completed",
          })
      );
    } finally {
      setBusy(false);
    }
  };

  const openFile = (fileId: string) => {
    if (!companyId || !fileId) return;
    dispatch(
      setModalxOptions({
        title: t("view_document_preview", {
          defaultValue: "Document Preview",
        }),
      })
    );
    dispatch(setModalxStyle("large"));
    dispatch(setModalxComponent("PdfView"));
    dispatch(
      setModalxComponentProps({
        id: fileId,
        companyId,
      })
    );
  };

  const steps = useMemo(
    () => [
      {
        id: "training" as const,
        label: t("training_step_materials", { defaultValue: "Training" }),
        done: trainingCompleted,
      },
      {
        id: "test" as const,
        label: t("training_step_questions", { defaultValue: "Test" }),
        done: assignment?.status === "passed",
        locked: !canAccessTest,
      },
    ],
    [t, trainingCompleted, canAccessTest, assignment?.status]
  );

  if (loading) {
    return (
      <Page
        fullWidth
        title={t("training_test", { defaultValue: "Training test" })}
      >
        <div className="flex justify-center py-20">
          <Spinner size="large" />
        </div>
      </Page>
    );
  }

  if (!assignment) {
    return (
      <Page
        fullWidth
        title={t("training_test", { defaultValue: "Training test" })}
        backAction={{
          onAction: () => navigate(`/app/${companyId}/my-work/training`),
        }}
      >
        <Banner tone="critical">{error || "Not found"}</Banner>
      </Page>
    );
  }

  return (
    <Page
      fullWidth
      title={assignment.test?.name || t("training_test")}
      backAction={{
        onAction: () => navigate(`/app/${companyId}/my-work/training`),
      }}
      titleMetadata={
        <PolarisBadge
          tone={
            assignment.status === "passed"
              ? "success"
              : assignment.status === "failed"
                ? "critical"
                : undefined
          }
        >
          {statusLabel(t, assignment.status)}
        </PolarisBadge>
      }
    >
      <div className="space-y-4 pb-10">
        {error && (
          <Banner tone="critical" onDismiss={() => setError("")}>
            {error}
          </Banner>
        )}

        <div className="rounded-lg border border-zinc-200 bg-white p-3 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
          <div className="flex flex-wrap items-center gap-2">
            {steps.map((s, idx) => {
              // Only the step being shown is solid; a finished one is a pale green.
              const isCurrent = currentStep === s.id;
              const isDone = s.done;
              const locked = !!(s as { locked?: boolean }).locked;
              return (
                <button
                  key={s.id}
                  type="button"
                  disabled={locked && !isDone}
                  onClick={() => {
                    if (locked && !isDone) return;
                    goToStep(s.id);
                  }}
                  className={`min-w-[100px] rounded-lg px-3 py-1.5 text-center transition-colors max-md:min-h-11 ${
                    isCurrent
                      ? "bg-blue-500 text-white"
                      : isDone
                        ? "bg-emerald-50 text-emerald-800 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-200"
                        : locked
                          ? "cursor-not-allowed bg-zinc-100 text-zinc-400 dark:bg-zinc-800"
                          : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-300"
                  }`}
                >
                  <p className={`text-sm ${isCurrent ? "font-bold" : ""}`}>
                    {`${idx + 1}. ${s.label}`}
                    {locked && !isDone
                      ? ` (${t("locked", { defaultValue: "locked" })})`
                      : ""}
                  </p>
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-xs text-zinc-500">
            {t("worker_training_steps_hint", {
              defaultValue:
                "Complete training materials first. The test unlocks after you mark training as completed.",
            })}
          </p>
        </div>

        {currentStep === "training" && (
          <Card>
            <div className="p-4 space-y-4">
              <div>
                <Text as="h2" variant="headingMd">
                  {t("training_materials", {
                    defaultValue: "Training materials",
                  })}
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  {t("worker_training_materials_hint", {
                    defaultValue:
                      "Review the documents below, then mark training as completed to unlock the test.",
                  })}
                </Text>
              </div>

              {materialsCount === 0 ? (
                <Banner tone="info">
                  {t("no_materials_for_training", {
                    defaultValue:
                      "No training documents were attached. You can continue to the test.",
                  })}
                </Banner>
              ) : (
                <div className="divide-y divide-zinc-200 rounded-xl border border-zinc-200 dark:divide-zinc-700 dark:border-zinc-700">
                  {materials.map((m) => (
                    <div
                      key={m.id}
                      className="flex flex-wrap items-center justify-between gap-2 px-4 py-3"
                    >
                      <Text as="span" variant="bodyMd">
                        {m.fileName}
                      </Text>
                      <div className="flex flex-wrap gap-2">
                        <PolarisButton
                          size="slim"
                          onClick={() => openFile(m.fileId)}
                        >
                          {t("view", { defaultValue: "View" })}
                        </PolarisButton>
                        {/* The preview box is small on a phone; the browser's own PDF viewer zooms and scrolls. */}
                        <PolarisButton
                          size="slim"
                          url={`${API_URL}/${companyId}/files/download/${m.fileId}?mode=view`}
                          external
                        >
                          {t("training_open_pdf", { defaultValue: "Open PDF" })}
                        </PolarisButton>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {trainingCompleted ? (
                <Banner tone="success">
                  {t("training_already_completed", {
                    defaultValue:
                      "Training is marked as completed. You can take the test.",
                  })}
                </Banner>
              ) : null}

              <div className="flex flex-wrap justify-end gap-2">
                {!trainingCompleted ? (
                  <PolarisButton
                    variant="primary"
                    loading={busy}
                    onClick={handleCompleteTraining}
                  >
                    {t("mark_training_completed", {
                      defaultValue: "Mark training completed",
                    })}
                  </PolarisButton>
                ) : (
                  <PolarisButton
                    variant="primary"
                    onClick={() => goToStep("test")}
                  >
                    {t("go_to_test", { defaultValue: "Go to test" })}
                  </PolarisButton>
                )}
              </div>
            </div>
          </Card>
        )}

        {currentStep === "test" && (
          <Card>
            <div className="p-4 space-y-4">
              <Text as="h2" variant="headingMd">
                {t("training_test", { defaultValue: "Training test" })}
              </Text>

              {!canAccessTest ? (
                <Banner tone="warning">
                  {t("complete_training_to_unlock_test", {
                    defaultValue:
                      "Complete the training step first to unlock the test.",
                  })}
                  <div className="mt-2">
                    <PolarisButton onClick={() => goToStep("training")}>
                      {t("back_to_training_step", {
                        defaultValue: "Back to training",
                      })}
                    </PolarisButton>
                  </div>
                </Banner>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-3">
                    <div className="rounded-lg border border-zinc-200 p-3 dark:border-zinc-700">
                      <p className="font-medium">
                        {t("duration_minutes", {
                          defaultValue: "Duration (min)",
                        })}
                      </p>
                      <p>{assignment.test?.durationMinutes ?? "—"}</p>
                    </div>
                    <div className="rounded-lg border border-zinc-200 p-3 dark:border-zinc-700">
                      <p className="font-medium">
                        {t("pass_percent", { defaultValue: "Pass %" })}
                      </p>
                      <p>{assignment.test?.passPercent ?? "—"}%</p>
                    </div>
                    <div className="rounded-lg border border-zinc-200 p-3 dark:border-zinc-700">
                      <p className="font-medium">
                        {t("attempts", { defaultValue: "Attempts" })}
                      </p>
                      <p>{assignment.attemptsCount ?? 0}</p>
                    </div>
                  </div>

                  {assignment.test?.description ? (
                    <Text as="p" variant="bodyMd">
                      {assignment.test.description}
                    </Text>
                  ) : null}

                  <div className="flex flex-wrap justify-end gap-2">
                    {(assignment.attemptsCount || 0) > 0 ? (
                      <PolarisButton
                        onClick={() =>
                          navigate(
                            `/app/${companyId}/my-work/training/history/${assignmentId}`
                          )
                        }
                      >
                        {t("view_history", { defaultValue: "History" })}
                      </PolarisButton>
                    ) : null}
                    {assignment.openAttempt ? (
                      <PolarisButton
                        variant="primary"
                        onClick={() =>
                          navigate(
                            `/app/${companyId}/my-work/training/take/${assignmentId}`
                          )
                        }
                      >
                        {t("continue_test", {
                          defaultValue: "Continue test",
                        })}
                      </PolarisButton>
                    ) : assignment.canStart ? (
                      <PolarisButton
                        variant="primary"
                        onClick={() =>
                          navigate(
                            `/app/${companyId}/my-work/training/take/${assignmentId}`
                          )
                        }
                      >
                        {t("start_test", { defaultValue: "Start test" })}
                      </PolarisButton>
                    ) : assignment.status === "passed" ? (
                      <Banner tone="success">
                        {t("test_passed", { defaultValue: "Passed" })}
                      </Banner>
                    ) : (
                      <Banner tone="info">
                        {t("cannot_start_test", {
                          defaultValue: "You cannot start this test right now.",
                        })}
                      </Banner>
                    )}
                  </div>
                </>
              )}
            </div>
          </Card>
        )}
      </div>
    </Page>
  );
}
